import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { webConfig } from '../config';
import { clearDevicePush, PUSH_OWNER_KEY } from '../lib/push';
import { beginLogin, refreshTokens, type TokenSet } from './oauth';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  /** Changes on explicit login/logout, independently of token refresh or profile requests. */
  sessionVersion: number;
  login: () => void;
  logout: () => void;
  getToken: () => Promise<string | null>;
  setSession: (tokens: TokenSet) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);
// Refresh token in localStorage so login persists across reloads AND tab/browser
// restarts (until the AS refresh-token TTL, ~14d). Access token stays in memory
// only. Trade-off: localStorage is XSS-readable — acceptable for a first-party
// internal app. Refreshes are single-flighted (see doRefresh) because the AS runs
// rotateRefreshToken: two concurrent refreshes with the same token trip
// reuse-detection and the AS revokes the whole grant.
const REFRESH_KEY = 'almanac.refresh_token';

/** Unverified JWT identity is only a browser UX hint; the API remains the authorizer. */
function tokenSubject(token?: string): string | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[0] || !parts[2]) return null;
    const encoded = parts[1]!;
    const payload: unknown = JSON.parse(
      atob(
        encoded.replaceAll('-', '+').replaceAll('_', '/') +
          '='.repeat((4 - (encoded.length % 4)) % 4),
      ),
    );
    if (
      payload &&
      typeof payload === 'object' &&
      'sub' in payload &&
      typeof payload.sub === 'string' &&
      payload.sub.length > 0
    )
      return payload.sub;
  } catch {
    /* Missing/malformed hints require cleanup. */
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(webConfig.devAuth ? 'authenticated' : 'loading');
  const [sessionVersion, setSessionVersion] = useState(0);
  const tokenRef = useRef<TokenSet | null>(null);
  const sessionGeneration = useRef(0);
  const refreshInflight = useRef(new Map<string, Promise<TokenSet>>());

  const applyTokens = useCallback((tokens: TokenSet) => {
    tokenRef.current = tokens;
    if (tokens.refreshToken) localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  }, []);

  // Single-flight token refresh: concurrent callers (bootstrap + data fetches)
  // share one in-flight request so a rotating refresh token is never used twice.
  const doRefresh = useCallback(
    (rt: string): Promise<TokenSet> => {
      const pending = refreshInflight.current.get(rt);
      if (pending) return pending;
      const generation = sessionGeneration.current;
      const initiatingToken = tokenRef.current?.accessToken;
      const initiatingSubject = initiatingToken
        ? tokenSubject(initiatingToken)
        : localStorage.getItem(PUSH_OWNER_KEY);
      const flight = { request: null as Promise<TokenSet> | null };
      flight.request = (async () => {
        try {
          const next = await refreshTokens(rt);
          if (generation !== sessionGeneration.current) {
            const current = tokenRef.current;
            const resultSubject = tokenSubject(next.accessToken);
            // Bootstrap can lack an identity hint. Sharing is safe only when the
            // current session still owns this token and matches the AS result.
            if (
              !current ||
              current.refreshToken !== rt ||
              !resultSubject ||
              tokenSubject(current.accessToken) !== resultSubject ||
              (initiatingToken && !initiatingSubject) ||
              (initiatingSubject && initiatingSubject !== resultSubject)
            ) {
              throw new Error('Session changed during refresh');
            }
          }
          applyTokens(next);
          return next;
        } finally {
          if (refreshInflight.current.get(rt) === flight.request)
            refreshInflight.current.delete(rt);
        }
      })();
      refreshInflight.current.set(rt, flight.request);
      return flight.request;
    },
    [applyTokens],
  );

  const setSession = useCallback(
    (tokens: TokenSet) => {
      const previous = tokenRef.current;
      const previousSub = previous
        ? tokenSubject(previous.accessToken)
        : localStorage.getItem(PUSH_OWNER_KEY);
      const nextSub = tokenSubject(tokens.accessToken);
      sessionGeneration.current += 1;
      if (!nextSub || !previousSub || nextSub !== previousSub)
        void clearDevicePush(previous?.accessToken);
      applyTokens(tokens);
      setSessionVersion((version) => version + 1);
      setStatus('authenticated');
    },
    [applyTokens],
  );

  const getToken = useCallback(async (): Promise<string | null> => {
    if (webConfig.devAuth) return null; // API dev-bypass authorizes without a token
    const current = tokenRef.current;
    if (current && current.expiresAt - Date.now() > 30_000) return current.accessToken;
    const rt = current?.refreshToken ?? localStorage.getItem(REFRESH_KEY);
    if (!rt) return null;
    const generation = sessionGeneration.current;
    try {
      const next = await doRefresh(rt);
      return next.accessToken;
    } catch {
      if (generation !== sessionGeneration.current) return tokenRef.current?.accessToken ?? null;
      localStorage.removeItem(REFRESH_KEY);
      tokenRef.current = null;
      void clearDevicePush();
      setStatus('unauthenticated');
      return null;
    }
  }, [doRefresh]);

  const login = useCallback(() => {
    void beginLogin();
  }, []);

  const logout = useCallback(() => {
    sessionGeneration.current += 1;
    void clearDevicePush(tokenRef.current?.accessToken);
    setSessionVersion((version) => version + 1);
    tokenRef.current = null;
    localStorage.removeItem(REFRESH_KEY);
    setStatus('unauthenticated');
  }, []);

  useEffect(() => {
    if (webConfig.devAuth) return;
    const rt = localStorage.getItem(REFRESH_KEY);
    if (!rt) {
      void clearDevicePush();
      setStatus('unauthenticated');
      return;
    }
    let cancelled = false;
    const generation = sessionGeneration.current;
    void (async () => {
      try {
        await doRefresh(rt);
        if (!cancelled && generation === sessionGeneration.current) setStatus('authenticated');
      } catch {
        if (!cancelled && generation === sessionGeneration.current) {
          void clearDevicePush();
          localStorage.removeItem(REFRESH_KEY);
          setStatus('unauthenticated');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doRefresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, sessionVersion, login, logout, getToken, setSession }),
    [status, sessionVersion, login, logout, getToken, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
